import React, { useContext, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { UserStateContext } from 'context/user-state-context';
import { ImpersonationBanner } from '../impersonation-banner/impersonation-banner.component';
const BANNER_MIN_HEIGHT_PX = 28;

/**
 * App-level top banner stack. Sits above the sidebar + routes as fixed chrome.
 * Add new global banners by dropping another component below `ImpersonationBanner`
 * and OR-ing its "active" signal into `isAnyBannerActive`.
 */
export const TopBanner = () => {
  const {
    impersonatingAsCustomer
  } = useContext(UserStateContext);
  const isAnyBannerActive = !!impersonatingAsCustomer;
  const bannerRef = useRef(null);

  // `.root` is `position: absolute; top: 0` — shift it down to leave room for
  // the fixed banner strip. The banner wraps taller on narrow screens, so the
  // offset is measured from the rendered height rather than assumed. The same
  // measurement feeds `--top-banner-height` (consumed by the sidebar and the
  // MuiDrawer rules in main.scss) and `body.has-top-banner` for portal chrome.
  useEffect(() => {
    const root = document.querySelector('.root');
    const clear = () => {
      if (root) root.style.top = '';
      document.body.classList.remove('has-top-banner');
      document.body.style.removeProperty('--top-banner-height');
    };
    if (!isAnyBannerActive) {
      clear();
      return clear;
    }
    document.body.classList.add('has-top-banner');
    const apply = () => {
      const height = bannerRef.current?.offsetHeight || BANNER_MIN_HEIGHT_PX;
      if (root) root.style.top = `${height}px`;
      document.body.style.setProperty('--top-banner-height', `${height}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    if (bannerRef.current) observer.observe(bannerRef.current);
    return () => {
      observer.disconnect();
      clear();
    };
  }, [isAnyBannerActive]);
  if (!isAnyBannerActive) return null;
  return <Box ref={bannerRef} sx={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    minHeight: `${BANNER_MIN_HEIGHT_PX}px`,
    zIndex: 1300,
    display: 'flex'
  }}>
      <ImpersonationBanner />
    </Box>;
};